import { observer } from 'mobx-react-lite'
import { type OCRScopingJobMainPanelViewModel } from '@/features/jobs/scoping/OCRScopingJobMainPanelViewModel'
import { Step } from '@/features/jobs/drafting/components/JobCreation/JobCreationFormView/Step'
import { type QuestionStepViewModel } from '@/features/jobs/drafting/IntakeQuestionsViewModel'
import { AnswerEditorRendererContainer } from '@/features/jobs/questions/components/AnswerEditorRendererContainer'
import { BoolQuestionAnswerEditorViewModel } from '@/features/jobs/questions/bool/BoolQuestionAnswerEditorViewModel'
import { BoolSelectQuestionAnswerEditorContainer } from '@/features/jobs/questions/bool/components/BoolQuestionAnswerEditor/BoolSelectQuestionAnswerEditorContainer'
import { SelectQuestionAnswerEditorViewModel } from '@/features/jobs/questions/select/SelectQuestionAnswerEditorViewModel'
import { AdvancedSelectContainer } from '@/features/jobs/questions/select/components/AdvancedSelect/AdvancedSelectContainer'
import { UnknownQuestionAnswerEditorViewModel } from '@/features/jobs/questions/unknown/UnknownQuestionAnswerEditorViewModel'
import { OCRScopeJobDetailBodyLegendInformation } from '@/features/jobs/scoping/components/OCRScopeJobDetailBodyLegendInformation'

export interface OCRScopeJobDetailBodyLegendInformationContainerProps {
  vm: OCRScopingJobMainPanelViewModel
}

export const OCRScopeJobDetailBodyLegendInformationContainer = observer(
  function OCRScopeJobDetailBodyLegendInformationContainer({
    vm,
  }: OCRScopeJobDetailBodyLegendInformationContainerProps) {
    return (
      <OCRScopeJobDetailBodyLegendInformation>
        {vm.editor.intakeQuestions.stepAccessors.map((stepAccessor) => (
          <Step
            key={stepAccessor.step.stepId}
            title={stepAccessor.step.outlineDisplay.title}
            isLast={stepAccessor.step.position.last}
            descriptionHtml={stepAccessor.step.descriptionHtml}
            active={stepAccessor.step.isActive}
            becomeActive$={stepAccessor.step.becomeActive$}
            onBecomeVisible={stepAccessor.step.becomeVisible}
            onBecomeHidden={stepAccessor.step.becomeHidden}
            testId={`step-${stepAccessor.step.stepId}`}
            compact
          >
            <QuestionEditorContainer stepAccessor={stepAccessor} />
          </Step>
        ))}
      </OCRScopeJobDetailBodyLegendInformation>
    )
  },
)

interface QuestionEditorContainerProps {
  stepAccessor: QuestionStepViewModel
}

/**
 * Unlike the job creation dialog, scoping answers yes/no and select questions
 * with dropdowns.
 */
const QuestionEditorContainer = observer(function QuestionEditorContainer({
  stepAccessor,
}: QuestionEditorContainerProps) {
  const editorVm = stepAccessor.question.editor
  const props = {
    questionId: stepAccessor.question.id,
    isDisabled: stepAccessor.disabled,
    focus$: stepAccessor.step.becomeActive$,
    onFocus: stepAccessor.step.onBecameActive,
  }

  switch (true) {
    case editorVm instanceof BoolQuestionAnswerEditorViewModel:
      return (
        <BoolSelectQuestionAnswerEditorContainer
          editorVm={editorVm}
          {...props}
        />
      )
    case editorVm instanceof SelectQuestionAnswerEditorViewModel:
      return <AdvancedSelectContainer editorVm={editorVm} {...props} />
    case editorVm instanceof UnknownQuestionAnswerEditorViewModel:
      return null
    default:
      return <AnswerEditorRendererContainer editorVm={editorVm} {...props} />
  }
})
